/**
 * Web Target Settings Modal
 * Modal dialog for configuring web target auth settings, login detection,
 * request variables ({{KEY}} substitution) and OAuth2 client credentials.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSave, faCog, faKey, faPlus, faTrash, faSync, faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import WebTargetAuthSettings from './WebTargetAuthSettings';
import webTargetService from '../../services/webTargetService';
import { toast } from 'react-toastify';

const WebTargetSettingsModal = ({ webTarget, onClose, onSave, isDarkMode }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [settings, setSettings] = useState({
    mode: 'auto',
    stalenessDetection: {
      enabled: true,
      autoDetectBehaviorChange: true,
      maxAgeHours: 0,
      warningAgeHours: 24,
      criticalAgeHours: 168
    }
  });
  const [loginDetectionEnabled, setLoginDetectionEnabled] = useState(true);
  const [autoCreateEnabled, setAutoCreateEnabled] = useState(true);
  const [saving, setSaving] = useState(false);

  // Variables & OAuth2 state
  const [variables, setVariables] = useState([]);
  const [oauth2, setOauth2] = useState({ tokenUrl: '', clientId: '', clientSecret: '', scope: '' });
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarValue, setNewVarValue] = useState('');
  const [newVarSecret, setNewVarSecret] = useState(false);
  const [fetchingToken, setFetchingToken] = useState(false);
  const [savingOauth, setSavingOauth] = useState(false);
  const [tokenInfo, setTokenInfo] = useState(null);
  const [revealedVars, setRevealedVars] = useState(new Set());

  useEffect(() => {
    if (webTarget) {
      setSettings(webTarget.authProfileUpdateBehavior || {
        mode: 'auto',
        stalenessDetection: {
          enabled: true,
          autoDetectBehaviorChange: true,
          maxAgeHours: 0,
          warningAgeHours: 24,
          criticalAgeHours: 168
        }
      });
      setLoginDetectionEnabled(webTarget.loginDetectionConfig?.enabled !== false);
      setAutoCreateEnabled(webTarget.loginDetectionConfig?.autoCreateProfileFromLogin !== false);
    }
  }, [webTarget]);

  const loadVariables = useCallback(async () => {
    if (!webTarget?._id) return;
    try {
      const data = await webTargetService.getVariables(webTarget._id);
      setVariables(data.variables || []);
      if (data.oauth2Config) {
        setOauth2({
          tokenUrl: data.oauth2Config.tokenUrl || '',
          clientId: data.oauth2Config.clientId || '',
          clientSecret: data.oauth2Config.clientSecret || '',
          scope: data.oauth2Config.scope || ''
        });
        if (data.oauth2Config.hasToken) {
          setTokenInfo({
            expiresAt: data.oauth2Config.tokenExpiresAt,
            lastFetchedAt: data.oauth2Config.lastFetchedAt
          });
        }
      }
    } catch (err) {
      console.error('Failed to load variables:', err);
    }
  }, [webTarget?._id]);

  useEffect(() => { loadVariables(); }, [loadVariables]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const updateData = {
        authProfileUpdateBehavior: settings,
        loginDetectionConfig: {
          enabled: loginDetectionEnabled,
          autoCreateProfileFromLogin: autoCreateEnabled
        }
      };
      await webTargetService.update(webTarget._id, updateData);
      toast.success('Settings saved');
      if (onSave) onSave(updateData);
      onClose();
    } catch (error) {
      toast.error('Failed to save: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAddVariable = async () => {
    if (!newVarKey.trim()) return;
    try {
      await webTargetService.setVariable(webTarget._id, { key: newVarKey.trim(), value: newVarValue, isSecret: newVarSecret });
      setNewVarKey(''); setNewVarValue(''); setNewVarSecret(false);
      loadVariables();
      toast.success(`Variable {{${newVarKey.trim()}}} saved`);
    } catch (err) { toast.error('Failed to save variable'); }
  };

  const handleDeleteVariable = async (key) => {
    try {
      await webTargetService.deleteVariable(webTarget._id, key);
      loadVariables();
      toast.success(`Variable {{${key}}} deleted`);
    } catch (err) { toast.error('Failed to delete variable'); }
  };

  const handleSaveOAuth2 = async () => {
    try {
      setSavingOauth(true);
      await webTargetService.saveOAuth2Config(webTarget._id, oauth2);
      toast.success('OAuth2 config saved');
    } catch (err) { toast.error('Failed to save OAuth2 config'); }
    finally { setSavingOauth(false); }
  };

  const handleFetchToken = async (viaServer = false) => {
    try {
      setFetchingToken(true);
      await webTargetService.saveOAuth2Config(webTarget._id, oauth2);

      if (!viaServer) {
        // Try fetching directly from the browser (uses the user's network/VPN)
        try {
          const body = new URLSearchParams({
            grant_type: 'client_credentials',
            client_id: oauth2.clientId,
            client_secret: oauth2.clientSecret,
            ...(oauth2.scope ? { scope: oauth2.scope } : {})
          });
          const resp = await fetch(oauth2.tokenUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString()
          });
          if (!resp.ok) {
            const text = await resp.text();
            const cleanMsg = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 200);
            throw new Error(`${resp.status}: ${cleanMsg || resp.statusText}`);
          }
          const data = await resp.json();
          if (!data.access_token) throw new Error('Kein access_token in der Antwort');
          // Save the token as variable via our API
          await webTargetService.setVariable(webTarget._id, { key: 'TOKEN', value: data.access_token, isSecret: true, description: 'OAuth2 Bearer Token (Browser-Fetch)' });
          toast.success(`Token geholt (läuft ab in ${data.expires_in}s). {{TOKEN}} Variable aktualisiert.`);
          setTokenInfo({ expiresAt: data.expires_in ? new Date(Date.now() + data.expires_in * 1000).toISOString() : null, lastFetchedAt: new Date().toISOString() });
          loadVariables();
          return;
        } catch (browserErr) {
          // CORS or network error from browser — fall back to server
          if (browserErr.message?.includes('Failed to fetch') || browserErr.message?.includes('NetworkError') || browserErr.name === 'TypeError') {
            toast.info('Browser-Fetch blockiert (CORS) — versuche über Server...');
          } else {
            // Real error (403, 401, etc.) — don't retry via server, show the error
            toast.error('Token-Fetch fehlgeschlagen: ' + browserErr.message);
            return;
          }
        }
      }

      // Server-side fetch
      const result = await webTargetService.fetchToken(webTarget._id);
      toast.success(`Token geholt (läuft ab in ${result.expiresIn}s). {{TOKEN}} Variable aktualisiert.`);
      setTokenInfo({ expiresAt: result.expiresAt, lastFetchedAt: new Date().toISOString() });
      loadVariables();
    } catch (err) {
      const raw = err.response?.data?.details || err.response?.data?.error || err.message;
      const cleanMsg = typeof raw === 'string' ? raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().substring(0, 200) : String(raw);
      toast.error('Token-Fetch fehlgeschlagen: ' + cleanMsg);
    } finally { setFetchingToken(false); }
  };

  const inputCls = `w-full px-3 py-1.5 text-sm rounded border ${isDarkMode ? 'bg-gray-700 border-gray-600 text-gray-200' : 'bg-white border-gray-300 text-gray-900'}`;
  const tabCls = (t) => `px-4 py-2 text-sm font-medium rounded-t border-b-2 ${activeTab === t
    ? (isDarkMode ? 'border-blue-400 text-blue-400' : 'border-blue-600 text-blue-600')
    : (isDarkMode ? 'border-transparent text-gray-400 hover:text-gray-200' : 'border-transparent text-gray-500 hover:text-gray-700')
  }`;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className={`relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl ${
        isDarkMode ? 'bg-gray-800 text-gray-200' : 'bg-white text-gray-900'
      }`}>
        {/* Header */}
        <div className={`sticky top-0 z-10 flex items-center justify-between p-4 border-b ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FontAwesomeIcon icon={faCog} className="text-blue-500" />
            Web Target Settings
          </h2>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex gap-1 px-4 pt-2 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button className={tabCls('general')} onClick={() => setActiveTab('general')}>General</button>
          <button className={tabCls('variables')} onClick={() => setActiveTab('variables')}>
            <FontAwesomeIcon icon={faKey} className="mr-1.5" />Variables & OAuth2
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Web Target Info */}
          <div className={`p-3 rounded ${isDarkMode ? 'bg-gray-700/30' : 'bg-gray-50'}`}>
            <div className="text-sm font-medium mb-1">Web Target</div>
            <div className="text-xs text-gray-500">{webTarget?.baseUrl}</div>
          </div>

          {activeTab === 'general' && (
            <>
              <div>
                <label className={`block text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Login Detection
                </label>
                <div className="space-y-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={loginDetectionEnabled} onChange={(e) => setLoginDetectionEnabled(e.target.checked)} className="rounded" />
                    <span className="text-sm">Enable login detection</span>
                  </label>
                  {loginDetectionEnabled && (
                    <label className="flex items-center gap-2 cursor-pointer pl-6">
                      <input type="checkbox" checked={autoCreateEnabled} onChange={(e) => setAutoCreateEnabled(e.target.checked)} className="rounded" />
                      <span className="text-sm">Auto-create profiles from detected logins</span>
                    </label>
                  )}
                </div>
              </div>
              {autoCreateEnabled && loginDetectionEnabled && (
                <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <WebTargetAuthSettings settings={settings} onChange={setSettings} isDarkMode={isDarkMode} />
                </div>
              )}
            </>
          )}

          {activeTab === 'variables' && (
            <>
              {/* Variables section */}
              <div>
                <h3 className="text-sm font-medium mb-2">Request Variables</h3>
                <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Use <code className={`px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>{'{{KEY}}'}</code> in URLs, headers, or bodies. Replaced automatically when sending from the Repeater.
                </p>

                {variables.length > 0 && (
                  <div className="space-y-1 mb-3">
                    {variables.map(v => (
                      <div key={v.key} className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                        <code className="font-mono text-blue-500 min-w-[100px]">{`{{${v.key}}}`}</code>
                        <span className="flex-1 font-mono truncate">
                          {v.isSecret && !revealedVars.has(v.key) ? '••••••••' : v.value}
                        </span>
                        {v.isSecret && (
                          <button onClick={() => setRevealedVars(prev => {
                            const n = new Set(prev);
                            n.has(v.key) ? n.delete(v.key) : n.add(v.key);
                            return n;
                          })} className="text-gray-400 hover:text-gray-300 p-1" title={revealedVars.has(v.key) ? 'Hide' : 'Reveal'}>
                            <FontAwesomeIcon icon={revealedVars.has(v.key) ? faEyeSlash : faEye} className="text-xs" />
                          </button>
                        )}
                        <button onClick={() => handleDeleteVariable(v.key)} className="text-red-400 hover:text-red-300 p-1" title="Delete">
                          <FontAwesomeIcon icon={faTrash} className="text-xs" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className={`p-3 rounded border ${isDarkMode ? 'border-gray-600 bg-gray-700/30' : 'border-gray-200 bg-gray-50'}`}>
                  <div className="text-xs font-medium mb-2">Neue Variable hinzufügen</div>
                  <div className="grid grid-cols-[120px_1fr] gap-2 mb-2">
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Name</label>
                      <input className={inputCls} placeholder="z.B. TOKEN" value={newVarKey}
                        onChange={e => setNewVarKey(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
                        onKeyDown={e => e.key === 'Enter' && handleAddVariable()} />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-400 mb-1">Wert (Secret / Passwort / Token)</label>
                      <input className={inputCls} placeholder="Wert eingeben..." value={newVarValue}
                        onChange={e => setNewVarValue(e.target.value)}
                        type={newVarSecret ? 'password' : 'text'}
                        onKeyDown={e => e.key === 'Enter' && handleAddVariable()} />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-1.5 text-xs cursor-pointer">
                      <input type="checkbox" checked={newVarSecret} onChange={e => setNewVarSecret(e.target.checked)} className="rounded" />
                      Als Secret maskieren
                    </label>
                    <button onClick={handleAddVariable} disabled={!newVarKey.trim()}
                      className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm disabled:opacity-50 ml-auto">
                      <FontAwesomeIcon icon={faPlus} className="mr-1.5" />Hinzufügen
                    </button>
                  </div>
                </div>
              </div>

              {/* OAuth2 section */}
              <div className={`border-t pt-6 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <h3 className="text-sm font-medium mb-2">OAuth2 Client Credentials</h3>
                <p className={`text-xs mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Fetch a Bearer token automatically. The token is stored as the <code className={`px-1 py-0.5 rounded ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>{'{{TOKEN}}'}</code> variable.
                </p>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium mb-1">Token URL</label>
                    <input className={inputCls} placeholder="https://keycloak.example.com/realms/myapp/protocol/openid-connect/token"
                      value={oauth2.tokenUrl} onChange={e => setOauth2(p => ({...p, tokenUrl: e.target.value}))} />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">Client ID</label>
                      <input className={inputCls} value={oauth2.clientId} onChange={e => setOauth2(p => ({...p, clientId: e.target.value}))} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">Client Secret</label>
                      <input className={inputCls} type="password" value={oauth2.clientSecret}
                        onChange={e => setOauth2(p => ({...p, clientSecret: e.target.value}))} />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1">Scope (optional)</label>
                    <input className={inputCls} placeholder="openid" value={oauth2.scope}
                      onChange={e => setOauth2(p => ({...p, scope: e.target.value}))} />
                  </div>

                  {tokenInfo && (
                    <div className={`text-xs p-2 rounded ${isDarkMode ? 'bg-green-900/30 text-green-300' : 'bg-green-50 text-green-700'}`}>
                      Token active — fetched {tokenInfo.lastFetchedAt ? new Date(tokenInfo.lastFetchedAt).toLocaleString() : 'unknown'}
                      {tokenInfo.expiresAt && ` — expires ${new Date(tokenInfo.expiresAt).toLocaleString()}`}
                    </div>
                  )}

                  <div className="flex gap-2">
                    <button onClick={handleSaveOAuth2} disabled={savingOauth}
                      className={`px-3 py-1.5 rounded text-sm ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600 text-gray-200' : 'bg-gray-200 hover:bg-gray-300 text-gray-800'}`}>
                      <FontAwesomeIcon icon={faSave} className="mr-1.5" />
                      {savingOauth ? 'Saving...' : 'Save Config'}
                    </button>
                    <button onClick={() => handleFetchToken(false)} disabled={fetchingToken || !oauth2.tokenUrl || !oauth2.clientId}
                      className="px-3 py-1.5 bg-teal-600 hover:bg-teal-700 text-white rounded text-sm disabled:opacity-50"
                      title="Holt den Token direkt von deinem Browser (nutzt dein VPN/Netzwerk)">
                      <FontAwesomeIcon icon={faSync} className={`mr-1.5 ${fetchingToken ? 'animate-spin' : ''}`} />
                      {fetchingToken ? 'Wird geholt...' : 'Token holen (Browser)'}
                    </button>
                    <button onClick={() => handleFetchToken(true)} disabled={fetchingToken || !oauth2.tokenUrl || !oauth2.clientId}
                      className={`px-3 py-1.5 rounded text-sm disabled:opacity-50 ${isDarkMode ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-300 hover:bg-gray-400 text-gray-800'}`}
                      title="Holt den Token vom PurpleHQ-Server (falls dein Browser CORS blockiert)">
                      <FontAwesomeIcon icon={faSync} className={`mr-1.5 ${fetchingToken ? 'animate-spin' : ''}`} />
                      via Server
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className={`sticky bottom-0 flex items-center justify-end gap-3 p-4 border-t ${
          isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
        }`}>
          <button onClick={onClose} className={`px-4 py-2 rounded ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`} disabled={saving}>
            Cancel
          </button>
          {activeTab === 'general' && (
            <button onClick={handleSave} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded flex items-center gap-2" disabled={saving}>
              <FontAwesomeIcon icon={faSave} />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebTargetSettingsModal;
